/* eslint-disable sonarjs/no-duplicate-string */
import { ActionContext } from 'vuex';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import { RouteName } from '@src/app/router/constants/RouteName';
import appRouter from '@app/router';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import {
    createSimpleGetter,
    createSimpleMutator,
} from '@superbet-group/web.lib.vue-utilities';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { AccountFlagNames } from '@modules/userProfile/account/FeatureFlags';
import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import { authenticationService } from '@core/services/authentication/AuthenticationService';
import { KycStatus } from '@accountFederation/modules/idCardVerification/getid/types';
import { noModalsRoutes } from '@store/modules/ui/shared/modal/helpers';
import { getConfig } from '@superbet-group/web.lib.config';
import { shellLocalConfig } from '@src/config/shell/localConfig';

const hasQuitSessionKey = 'hasQuitKycThisSession';
const userGetterKey = 'data/authentication/user';

const defaultState: CaptiveKycState = {
    removeRouteInterceptor: undefined,
    popStateInterceptor: undefined,
    isCaptiveKycEnabledByFeatureFlag: false,
    hasQuitKycThisSession: clientStorageWithExpiry.get<boolean>(hasQuitSessionKey) || false,
    redirectUrl: undefined,
    isUnskippable: false,
};

const allowedNavigationsWhileCaptive = [
    RouteName.App.VerifyPhoneCaptive,
    RouteName.App.IdCardVerificationCaptive,
];

const SET_KYC_SESSION_STATUS = 'SET_KYC_SESSION_STATUS';
const SET_REMOVE_INTERCEPTOR_REFERENCE = 'SET_REMOVE_INTERCEPTOR_REFERENCE';
const SET_POPSTATE_INTERCEPTOR_REFERENCE = 'SET_POPSTATE_INTERCEPTOR_REFERENCE';
const SET_IS_CAPTIVE_KYC_ENABLED_BY_FEATURE_FLAG = 'SET_IS_CAPTIVE_KYC_ENABLED_BY_FEATURE_FLAG';
const SET_RETURN_URL = 'SET_RETURN_URL';
const SET_IS_UNSKIPPABLE = 'SET_IS_UNSKIPPABLE';

type FreeFromCaptiveKycOptions = {
    isUserChoice?: boolean;
    shouldRedirect?: boolean;
};

export const captiveKycStore = {
    namespaced: true,
    state: defaultState,
    getters: {
        hasCaptiveKyc: (_state: CaptiveKycState, _getters: any, _rootState: unknown, rootGetters: any) => {
            return getAccountConfig().hasKycCaptive
                && rootGetters[userGetterKey]?.isVerifiedIdCard !== true
                && rootGetters[userGetterKey]?.kycDetails?.status !== KycStatus.Expired; // Late stage addition to allow expired kyc to interact
        },
        hasCaptivePhoneNumber: (_state: CaptiveKycState, _getters: any, _rootState: unknown, rootGetters: any) => {
            return getAccountConfig().hasVerifyPhoneCaptive
                && rootGetters[userGetterKey]?.shouldVerifySms;
        },
        hasMissingDetails: (_state: CaptiveKycState, _getters: any, _rootState: unknown, rootGetters: any) => {
            return getAccountConfig().hasGenderAndNationalityRequirement ? !rootGetters[userGetterKey]?.nationality || !rootGetters[userGetterKey]?.gender : false;
        },
        hasIncompleteVerification: (state: CaptiveKycState, getters: any) => {
            return ((getters.hasCaptiveKyc || getters.hasCaptivePhoneNumber) && state.isCaptiveKycEnabledByFeatureFlag) || getters.hasMissingDetails;
        },
        hasQuitKycThisSession: createSimpleGetter<CaptiveKycState, 'hasQuitKycThisSession'>('hasQuitKycThisSession'),
        isUnskippable: createSimpleGetter<CaptiveKycState, 'isUnskippable'>('isUnskippable'),
    },
    mutations: {
        [SET_KYC_SESSION_STATUS]: createSimpleMutator<CaptiveKycState, 'hasQuitKycThisSession'>('hasQuitKycThisSession'),
        [SET_REMOVE_INTERCEPTOR_REFERENCE]: createSimpleMutator<CaptiveKycState, 'removeRouteInterceptor'>('removeRouteInterceptor'),
        [SET_POPSTATE_INTERCEPTOR_REFERENCE]: createSimpleMutator<CaptiveKycState, 'popStateInterceptor'>('popStateInterceptor'),
        [SET_IS_CAPTIVE_KYC_ENABLED_BY_FEATURE_FLAG]: createSimpleMutator<CaptiveKycState, 'isCaptiveKycEnabledByFeatureFlag'>('isCaptiveKycEnabledByFeatureFlag'),
        [SET_RETURN_URL]: createSimpleMutator<CaptiveKycState, 'redirectUrl'>('redirectUrl'),
        [SET_IS_UNSKIPPABLE]: createSimpleMutator<CaptiveKycState, 'isUnskippable'>('isUnskippable'),
    },
    actions: {
        initializeCaptiveKycStore: (context: CaptiveKycContext) => {
            featureFlagService.subscribeToFeatureChanges(AccountFlagNames.isCaptiveKycUnskippable, (isEnabled) => {
                context.commit(SET_IS_UNSKIPPABLE, isEnabled);
            }, false);

            featureFlagService.subscribeToFeatureChanges(AccountFlagNames.isCaptiveKycEnabled, (isEnabled) => {
                context.commit(SET_IS_CAPTIVE_KYC_ENABLED_BY_FEATURE_FLAG, isEnabled);
                if (isEnabled && !context.getters.hasQuitKycThisSession) {
                    if (context.getters.hasIncompleteVerification) {
                        context.dispatch('ui/user/messages/pauseNotifications', {}, { root: true });
                    }
                    context.dispatch('maybeRedirectToCaptiveKyc');
                } else {
                    context.dispatch('freeFromCaptiveKyc', { isUserChoice: false });
                }
            }, false);
        },
        showCaptiveKycEntry: (context: CaptiveKycContext) => {
            if (context.state.removeRouteInterceptor) {
                // Avoid modals when user is already in the process
                // To replace in future with isCaptive getter eventually;
                return;
            }

            if (
                context.rootGetters[userGetterKey]?.kycDetails?.isStatusPending === true
                || context.rootGetters[userGetterKey]?.kycDetails?.status === KycStatus.NeedsReview
            ) {
                context.dispatch('ui/modal/setModal', { code: modalCodes.captiveKycPending }, { root: true });
            } else if (context.getters.hasIncompleteVerification) {
                context.dispatch('ui/modal/setModal', { code: modalCodes.captiveKycRequired }, { root: true });
            }
        },
        maybeRedirectToCaptiveKyc: (context: CaptiveKycContext) => {
            if (
                !context.getters.hasIncompleteVerification
                || noModalsRoutes.has(context.rootGetters['navigation/route'].name) // Don't show on wiki
            ) {
                return;
            }

            const isOutsideOfCaptiveKyc = !allowedNavigationsWhileCaptive.includes(context.rootGetters['navigation/route'].name);

            if (
                isOutsideOfCaptiveKyc
                && context.rootGetters['navigation/route'].name !== RouteName.App.BrMigrationComplete
                && context.rootGetters['navigation/route'].name !== RouteName.App.Home
            ) {
                context.commit(SET_RETURN_URL, context.rootGetters['navigation/route'].fullPath);
            }

            if (context.getters.hasMissingDetails) {
                context.dispatch(
                    'navigation/push',
                    { name: RouteName.App.BrMigrationComplete },
                    { root: true },
                );
            } else if (context.getters.hasCaptiveKyc && isOutsideOfCaptiveKyc) {
                context.dispatch(
                    'navigation/push',
                    { name: RouteName.App.IdCardVerificationCaptive },
                    { root: true },
                );
            } else if (context.getters.hasCaptivePhoneNumber && isOutsideOfCaptiveKyc) {
                context.dispatch(
                    'navigation/push',
                    { name: RouteName.App.VerifyPhoneCaptive },
                    { root: true },
                );
            }

            if (context.getters.hasCaptiveKyc || context.getters.hasCaptivePhoneNumber) {
                context.dispatch('setCaptiveRouteInterceptor');
            }
        },
        setCaptiveRouteInterceptor: (context: CaptiveKycContext) => {
            if (context.state.removeRouteInterceptor) {
                return;
            }

            const popStateInterceptor = () => {
                context.dispatch('data/captiveKyc/maybeRedirectToCaptiveKyc', {}, { root: true });
                context.dispatch('showExitPromptIfNotMandatory');
            };

            window.addEventListener('popstate', popStateInterceptor);

            const removeRouteInterceptor = appRouter.beforeEach((to, _from, next) => {
                const user = context.rootGetters[userGetterKey];
                if (user && context.getters.hasIncompleteVerification && !allowedNavigationsWhileCaptive.includes(to.name || '')) {
                    context.dispatch('showExitPromptIfNotMandatory');
                } else {
                    next();
                }
            });

            context.commit(SET_REMOVE_INTERCEPTOR_REFERENCE, removeRouteInterceptor);
            context.commit(SET_POPSTATE_INTERCEPTOR_REFERENCE, popStateInterceptor);
        },
        showExitPromptIfNotMandatory: (context: CaptiveKycContext) => {
            // Hack to know we were on phone verification after popstate without extra route checks
            const hasMandatoryPhoneVerification = context.getters.hasCaptivePhoneNumber && !context.getters.hasCaptiveKyc;

            // Currently details and phone are mandatory
            if (
                context.getters.hasMissingDetails
                || hasMandatoryPhoneVerification
                || context.state.isUnskippable
            ) {
                return;
            }

            context.dispatch('ui/modal/setModal', {
                code: modalCodes.exitKycVerificationCaptive,
            }, { root: true });
        },
        removeRouteInterceptor: (context: CaptiveKycContext) => {
            if (context.state.popStateInterceptor) {
                window.removeEventListener('popstate', context.state.popStateInterceptor);
            }
            context.state.removeRouteInterceptor?.();
            context.commit(SET_REMOVE_INTERCEPTOR_REFERENCE, undefined);
            context.commit(SET_POPSTATE_INTERCEPTOR_REFERENCE, undefined);
        },
        freeFromCaptiveKyc: (
            context: CaptiveKycContext,
            opts: FreeFromCaptiveKycOptions = {
                isUserChoice: true,
                shouldRedirect: false,
            }) => {
            if (opts.isUserChoice !== false) {
                context.commit(SET_KYC_SESSION_STATUS, true);
                clientStorageWithExpiry.set(hasQuitSessionKey, true);
                if (
                    getConfig().countrySpecific.isResponsibleGamblingDisplayedOnLogin
                    && !context.rootGetters['data/authentication/hasAcceptedResponsibleGambling']
                ) {
                    context.dispatch('ui/modal/addModalToEnd', {
                        code: shellLocalConfig.responsibleGamblingLoginModal!,
                        isClosable: false,
                    }, { root: true });
                }
            }

            context.dispatch('removeRouteInterceptor');
            context.dispatch('ui/user/messages/resumeNotifications', {}, { root: true });

            if (
                getAccountConfig().isKycSuccessRedirectToDepositEnabled
                && !context.getters.hasIncompleteVerification
            ) {
                context.commit(SET_RETURN_URL, undefined);
                return;
            }

            if (opts.shouldRedirect) {
                if (!context.state.redirectUrl) {
                    context.dispatch('navigation/push', { name: RouteName.App.Home }, { root: true });
                } else {
                    context.dispatch('navigation/push', { path: context.state.redirectUrl }, { root: true });
                    context.commit(SET_RETURN_URL, undefined);
                }
            }
        },
        clearKycSessionStatus: (context: CaptiveKycContext) => {
            context.commit(SET_KYC_SESSION_STATUS, false);
            clientStorageWithExpiry.remove(hasQuitSessionKey);
        },
        fetchKycDetails: async (context: CaptiveKycContext) => {
            try {
                const kycDetails = await authenticationService.getKycDetails(
                    getAccountConfig().useKycLocalStoragePendingState && context.rootGetters[userGetterKey]
                        ? context.rootGetters[userGetterKey]
                        : undefined,
                );

                context.dispatch('data/authentication/setUserData', {
                    key: 'kycDetails',
                    value: kycDetails,
                }, { root: true });
            } catch (e) {
                // Fail silently
            }
        },
    },
};

export type CaptiveKycStore = typeof captiveKycStore;

type CaptiveKycContext = ActionContext<CaptiveKycState, unknown>;

type CaptiveKycState = {
    removeRouteInterceptor?: () => void;
    popStateInterceptor?: () => void;
    hasQuitKycThisSession: boolean;
    isCaptiveKycEnabledByFeatureFlag: boolean;
    redirectUrl?: string;
    isUnskippable: boolean;
};
